import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { navigate } from 'gatsby';
import { complement, curry, equals, forEachObjIndexed } from 'ramda';
import React, { FC, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';

import Checkbox from '@ui/forms/Checkbox';
import TextInput from '@ui/forms/TextInput';

import NewInputWrapper from '@ui/molecules/NewInputWrapper';
import SubmitButton, { ISubmitButtonProps } from '@ui/molecules/SubmitButton';

import fullWidth from '@ui/styled/FullWidth';
import spaced from '@ui/styled/Spaced';

import { useMemberValidation } from '@hooks/useMemberValidation';

import { uneditableFieldView } from '@helpers/Dashboard';

import { doesNotExist, exists } from '@utils/general';

import GraphQLv2Context from '@providers/GraphQLv2ContextProvider';

import { IPageProps } from '@folx-types/flow-steps';
import {
	IAccountInfoMutationData,
	IAccountInfoMutationResponse,
	IMemberData,
} from '@folx-types/member';

import ACCOUNT_INFO_MUTATION from '@graphql/mutations/AccountInfo.mutation.gql';
import UPDATE_LEGAL_NAME_MUTATION from '@graphql/mutations/updateLegalName.mutation.gql';

import { schema } from './AccountNameEdit.data';

export interface IAccountNameEditPageContent {
	pageHeader: string;
	pageInfo: string;
	firstNameLabel: string;
	lastNameLabel: string;
	legalFirstNameLabel: string;
	legalLastNameLabel: string;
	nameTooltipTitle: string;
	nameTooltip: string;
	legalNameTooltipTitle: string;
	legalNameTooltip: string;
	sameLegalNameFineprint: string;
	legalNameLabel: string;
	legalNameDisclaimer: string;
}

interface IAccountNameEditProps extends IPageProps {
	pageContent: IAccountNameEditPageContent;
	loading?: boolean;
	member?: IMemberData;
	noAccountName?: boolean;
}

const FullWidthSubmit = fullWidth<ISubmitButtonProps>(SubmitButton);
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);
const SpacedColumn = spaced<IFlexColumn>(FullWidthColumn);

const GradientColumn = styled(FullWidthColumn)`
	background: linear-gradient(
			322.11deg,
			rgba(129, 155, 247, 0.2) 6.91%,
			rgba(240, 163, 214, 0.148) 94.95%
		),
		linear-gradient(0deg, #f0eae2, #f0eae2);
	padding: 3.2rem 1.8rem 3rem;
	margin-left: -1.8rem;
	margin-right: -1.8rem;
	width: auto;
	border-radius: 0.8rem;
`;

const AccountNameEdit: FC<IAccountNameEditProps> = ({ member, noAccountName, pageContent }) => {
	const [submitting, setSubmitting] = useState(false);

	const {
		pageHeader,
		pageInfo,
		firstNameLabel,
		lastNameLabel,
		legalFirstNameLabel,
		legalLastNameLabel,
		nameTooltipTitle,
		nameTooltip,
		legalNameTooltipTitle,
		legalNameTooltip,
		sameLegalNameFineprint,
		legalNameLabel,
		legalNameDisclaimer,
	} = pageContent;

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		defaultValues: {},
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		register,
		reset,
		watch,
		formState: { errors },
	} = form;

	const {
		memberMetadata: {
			account: { isLegacyUser },
		},
	} = useMemberValidation();

	const nextRoute = isLegacyUser ? '/app/flows/membership/personal_info' : '..';

	const sameLegalName = watch('sameLegalName');

	useDeepCompareEffect(() => {
		if (doesNotExist(member)) {
			return;
		}

		reset({
			affirmingFirstName: member?.affirmingFirstName,
			affirmingLastName: member?.affirmingLastName,
			legalFirstName: member?.legalFirstName,
			legalLastName: member?.legalLastName,
		});
	}, [member]);

	const { client: graphQLv2Client } = useContext(GraphQLv2Context);

	const [updateAccountInfo] = useMutation<IAccountInfoMutationResponse>(ACCOUNT_INFO_MUTATION);

	const [updateMemberLegalName] = useMutation(UPDATE_LEGAL_NAME_MUTATION, {
		client: graphQLv2Client,
	});

	const updateLegalName = async (input: { legalFirstName: string; legalLastName: string }) => {
		await updateMemberLegalName({
			variables: input,
		}).catch((error) => {
			console.error(`Oof. We've run into a problem™ updating your information. ${error}`);
			throw error;
		});
	};

	const updateMemberAccountInfo = async (input: IAccountInfoMutationData) => {
		try {
			await updateAccountInfo({
				variables: input,
			});
		} catch (error) {
			console.error(`Oof. We've run into a problem™ updating your information. ${error}`);
			throw error;
		}
	};

	const saveAccountInfo = async (input: IAccountInfoMutationData['patch']) => {
		if (!member?.id) {
			throw new Error('Invalid user');
		}
		await updateMemberAccountInfo({
			clientMutationId: member?.id,
			memberId: member?.id,
			patch: input,
		});
	};

	const onSubmit = async (data: Record<string, unknown>) => {
		const notEqual = curry((second) => complement(equals(second)));

		let newData = {};
		const nameData = {
			affirmingFirstName: data.affirmingFirstName,
			affirmingLastName: data.affirmingLastName,
		};
		const legalNameData = data.sameLegalName
			? { legalFirstName: data.affirmingFirstName, legalLastName: data.affirmingLastName }
			: { legalFirstName: data.legalFirstName, legalLastName: data.legalLastName };

		forEachObjIndexed(async (value, key) => {
			const memberKey = member?.[key];
			const dataKey = data?.[key];
			const isNotEqual = notEqual(memberKey)(dataKey);
			newData = isNotEqual ? { ...newData, [key]: dataKey } : newData;
		})(nameData);

		let promises: Promise<ApolloQueryResult<IAccountInfoMutationResponse> | void>[] = [];
		promises = exists(newData) ? promises.concat(saveAccountInfo(newData)) : promises;
		promises = noAccountName
			? promises.concat(
					updateLegalName({
						legalFirstName: (legalNameData?.legalFirstName as string) ?? '',
						legalLastName: (legalNameData?.legalLastName as string) ?? '',
					})
			  )
			: promises;

		if (doesNotExist(promises)) {
			return;
		}

		setSubmitting(true);
		try {
			await Promise.all(promises);
		} catch (error) {
			console.log(error);
			// setAccountError(error);
			return;
		} finally {
			setSubmitting(false);
		}

		navigate(nextRoute);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<SpacedColumn gap="lg" marginBottom="xxl">
				<FullWidthColumn gap="sm">
					<HeadingText>{pageHeader}</HeadingText>
					<BasicText>{pageInfo}</BasicText>
				</FullWidthColumn>
				<GradientColumn gap="md">
					<FlexColumn gap="xs">
						<NewInputWrapper
							titleType="field"
							title={firstNameLabel}
							errorPos="bottom"
							tooltipDescription={nameTooltip}
							tooltipTitle={nameTooltipTitle}>
							<TextInput
								placeholder={firstNameLabel}
								error={!!errors.affirmingFirstName}
								errorLabel={errors?.affirmingFirstName?.message ?? ''}
								{...register('affirmingFirstName')}
								type="text"
								fullWidth
							/>
						</NewInputWrapper>
					</FlexColumn>
					<TextInput
						placeholder={lastNameLabel}
						label={lastNameLabel}
						error={!!errors.affirmingLastName}
						errorLabel={errors?.affirmingLastName?.message ?? ''}
						{...register('affirmingLastName')}
						type="text"
						fullWidth
					/>
					{noAccountName && (
						<Checkbox
							{...register('sameLegalName')}
							textSize="xxs"
							label={sameLegalNameFineprint}
						/>
					)}
				</GradientColumn>
				{noAccountName ? (
					<>
						<FlexColumn gap="xs">
							<NewInputWrapper
								titleType="field"
								title={legalFirstNameLabel}
								errorPos="bottom"
								tooltipDescription={legalNameTooltip}
								tooltipTitle={legalNameTooltipTitle}>
								<TextInput
									disabled={!!sameLegalName}
									error={!!errors.legalFirstName}
									errorLabel={errors?.legalFirstName?.message ?? ''}
									{...register('legalFirstName')}
									type="text"
									fullWidth
								/>
							</NewInputWrapper>
						</FlexColumn>
						<TextInput
							label={legalLastNameLabel}
							disabled={!!sameLegalName}
							error={!!errors.legalLastName}
							errorLabel={errors?.legalLastName?.message ?? ''}
							{...register('legalLastName')}
							type="text"
							fullWidth
						/>
					</>
				) : (
					<FullWidthColumn gap="sm">
						{uneditableFieldView(
							legalNameLabel,
							legalNameDisclaimer,
							member?.legalFirstName || member?.legalLastName
								? `${member?.legalFirstName} ${member?.legalLastName}`
								: undefined
						)}
					</FullWidthColumn>
				)}
			</SpacedColumn>
			<FullWidthSubmit label="Next" submitting={submitting} variant="secondary" />
		</form>
	);
};

export default AccountNameEdit;
